import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('subscribfy:cartItemPlanChanged', this.#handleSubscribfyCartChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('subscribfy:cartItemPlanChanged', this.#handleSubscribfyCartChange);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleSubscribfyCartChange = () => {
    // Refresh cart drawer content without page reload
    this.#refreshCartDrawer();
  };

  async #refreshCartDrawer() {
    try {
      // Find the cart drawer content element
      const cartDrawerContent = /** @type {HTMLElement | null} */ (this.querySelector('[data-section-id]'));
      if (!cartDrawerContent) return;

      const sectionId = cartDrawerContent.dataset.sectionId;
      if (!sectionId) return;

      // Fetch updated cart section
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (!response.ok) throw new Error('Failed to fetch cart section');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = /** @type {HTMLElement | null} */ (doc.querySelector(`[data-section-id="${sectionId}"]`));

      if (newContent && cartDrawerContent) {
        // Replace the content
        cartDrawerContent.innerHTML = newContent.innerHTML;
        
        // Dispatch cart update event to notify other components
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'subscribfy' }
        }));
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      // Fallback: reload the page if refresh fails
      window.location.reload();
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
